// The design lock for glyphs: every measure the drawings use, with what each
// part means. src/ui/glyph.ts draws from these numbers only, and
// tests/unit/glyph.test.ts locks the exact drawing of every glyph, so a
// change here shows up as a failing test until the version is bumped.
//
// What a glyph says, so it can be read without being told:
// - one ring per direction, inner ring first;
// - up: a bold arc over the ring's top half, with the ring's own mark on top;
// - down: the same turned over, mark beneath. A card's negative is its mirror;
// - level: a quiet ring with two small nodes at its sides, balanced;
// - folded: a band of light: the card now stands for all three values;
// - the Monad: every ring a thin band, a core, one ray per card it was made of;
// - the index row on the card's top edge spells the same value with the same
//   marks drawn small, outer ring first, so a buried card still reads.
// Card colour follows the outer ring: gold rises, bone is still, teal sinks,
// white is whole (src/config/palette.ts).

export const MARK_NAMES = ["bud", "fork", "crown", "cap"] as const
export type MarkName = (typeof MARK_NAMES)[number]

export type GlyphConfig = {
  readonly centre: number // viewBox is 0 0 2c 2c
  readonly singleRadius: number // a one-ring glyph's ring
  readonly innerRadius: number // the innermost ring when there are several
  readonly ringSpan: number // from the inner ring to the outer one
  readonly levelNode: number // radius of the two nodes on a level ring
  readonly marks: {
    readonly bud: { readonly stem: number; readonly at: number; readonly radius: number }
    readonly fork: { readonly stem: number; readonly spread: number; readonly reach: number }
    readonly crown: { readonly side: number; readonly middle: number; readonly angle: number }
    readonly cap: { readonly stem: number; readonly lift: number; readonly angle: number }
  }
  readonly index: {
    readonly gap: number
    readonly baseline: number
    readonly height: number
    readonly stalk: number
    readonly node: number
    readonly bud: number
    readonly fork: { readonly at: number; readonly spread: number; readonly reach: number }
    readonly crown: { readonly from: number; readonly spread: number; readonly short: number }
    readonly cap: { readonly stem: number; readonly spread: number; readonly bow: number; readonly lift: number }
    readonly folded: { readonly half: number; readonly top: number }
  }
  readonly monad: {
    readonly node: number
    readonly nodeTurn: number // each ring's three nodes turn this much further than the ring inside
    readonly core: number
    readonly rayGap: number
    readonly rays: readonly [number, number, number] // three lengths, in turn
  }
}

export const GLYPH = {
  centre: 50,
  singleRadius: 27,
  innerRadius: 10,
  ringSpan: 30,
  levelNode: 1.6,
  marks: {
    bud: { stem: 5.6, at: 7.5, radius: 2 },
    fork: { stem: 3.5, spread: 3.4, reach: 4.6 },
    crown: { side: 4.8, middle: 6.8, angle: 22 },
    cap: { stem: 4.2, lift: 7, angle: 14 },
  },
  index: {
    gap: 9,
    baseline: 12,
    height: 24,
    stalk: 7,
    node: 1.7,
    bud: 1.6,
    fork: { at: 4, spread: 2.6, reach: 4.4 },
    crown: { from: 2, spread: 2.8, short: 0.5 },
    cap: { stem: 5, spread: 3, bow: 3, lift: 0.6 },
    folded: { half: 2.4, top: 2.5 },
  },
  monad: { node: 1.5, nodeTurn: 40, core: 2.6, rayGap: 4.5, rays: [7, 3, 4.5] },
} as const satisfies GlyphConfig
